import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const TaskCreatePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const caseId = searchParams.get('case_id') || '';

    const [form, setForm] = useState({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        due_date: '',
        assigned_to: '',
        stage_id: '',
        case_id: caseId
    });
    const [warnMissingDeadline, setWarnMissingDeadline] = useState(false);

    const [stages, setStages] = useState([]);
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState([]);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await supabase.from('case_stages').select('id, title').order('sort_order');
                setStages(data || []);
            } catch (e) {
                console.error('Error loading stages', e);
            }
        })();
    }, []);

    const createTask = async () => {
        try {
            if (!form.title.trim()) {
                setToast({ type: 'error', message: 'Task title is required' });
                return;
            }

            if (!form.due_date) {
                setWarnMissingDeadline(true);
                // Allow proceed but warn
            } else {
                setWarnMissingDeadline(false);
            }

            setSaving(true);

            // 1. Create Task
            const { data: task, error } = await supabase
                .from('case_tasks')
                .insert([{
                    ...form,
                    case_id: form.case_id || null,
                    stage_id: form.stage_id || null
                }])
                .select()
                .single();

            if (error) throw error;

            // 2. Handle Attachments
            if (files.length > 0 && task) {
                for (const file of files) {
                    const path = `cases/${task.case_id || 'general'}/tasks/${task.id}/${Date.now()}_${file.name}`;
                    const { error: upErr } = await supabase.storage.from('documents').upload(path, file);

                    if (!upErr) {
                        // Add to case_attachments
                        await supabase.from('case_attachments').insert({
                            case_id: task.case_id,
                            task_id: task.id,
                            file_path: path,
                            file_name: file.name,
                            file_size: file.size
                        });

                        // Add note
                        if (task.case_id) {
                            const { data: pub } = supabase.storage.from('documents').getPublicUrl(path);
                            await supabase.from('case_notes').insert({
                                case_id: task.case_id,
                                content: `Task attachment: ${pub?.publicUrl || path} (${file.name})`
                            });
                        }
                    }
                }
            }

            setToast({ type: 'success', message: 'Task created successfully' });

            // Navigate back
            if (caseId) {
                navigate(`/admin/cases/${caseId}`);
            } else {
                navigate('/admin/tasks'); // Or wherever the main task list is
            }

        } catch (e) {
            console.error(e);
            setToast({ type: 'error', message: e.message || 'Failed to create task' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg border ${toast.type === 'success' ? 'bg-green-900/90 border-green-700 text-green-100' : 'bg-red-900/90 border-red-700 text-red-100'
                    }`}>
                    {toast.message}
                </div>
            )}

            <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-1">Create New Task</h1>
                        <p className="text-gray-400 text-sm">Add a new task to track progress</p>
                    </div>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>
                </div>

                <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden">
                    <div className="p-6 space-y-6">
                        {/* Main Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Task Title *</label>
                                <input
                                    type="text"
                                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    placeholder="e.g. Review Visa Documents"
                                    value={form.title}
                                    onChange={e => setForm({ ...form, title: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Status</label>
                                <select
                                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={form.status}
                                    onChange={e => setForm({ ...form, status: e.target.value })}
                                >
                                    <option value="todo">To Do</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="review">Review</option>
                                    <option value="done">Done</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Priority</label>
                                <select
                                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={form.priority}
                                    onChange={e => setForm({ ...form, priority: e.target.value })}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Due Date</label>
                                <input
                                    type="date"
                                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={form.due_date}
                                    onChange={e => setForm({ ...form, due_date: e.target.value })}
                                />
                                {warnMissingDeadline && !form.due_date && (
                                    <div className="mt-2 text-xs text-yellow-400">
                                        No due date set — this task won’t trigger Control Room deadlines.
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Assigned To (Email)</label>
                                <input
                                    type="email"
                                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="staff@example.com"
                                    value={form.assigned_to}
                                    onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                                />
                            </div>

                            {stages.length > 0 && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Stage</label>
                                    <select
                                        className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={form.stage_id}
                                        onChange={e => setForm({ ...form, stage_id: e.target.value })}
                                    >
                                        <option value="">Select Stage</option>
                                        {stages.map(s => (
                                            <option key={s.id} value={s.id}>{s.title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Case ID - usually hidden or read-only if passed via URL */}
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Case ID</label>
                                <input
                                    type="text"
                                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-400 bg-opacity-50 cursor-not-allowed"
                                    value={form.case_id}
                                    readOnly
                                    placeholder="Optional"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Description</label>
                            <textarea
                                rows={5}
                                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                placeholder="Detailed description of the task..."
                                value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })}
                            />
                        </div>

                        {/* Attachments */}
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Attachments</label>
                            <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-gray-500 transition-colors bg-gray-900/50">
                                <input
                                    type="file"
                                    multiple
                                    onChange={e => setFiles(Array.from(e.target.files || []))}
                                    className="hidden"
                                    id="file-upload"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center justify-center">
                                    <svg className="w-10 h-10 text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <span className="text-sm text-gray-400 font-medium">Click to upload files</span>
                                    <span className="text-xs text-gray-600 mt-1">or drag and drop</span>
                                </label>
                            </div>
                            {files.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {files.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded px-3 py-2">
                                            <span className="text-sm text-gray-300 truncate">{f.name}</span>
                                            <span className="text-xs text-gray-500">{(f.size / 1024).toFixed(1)} KB</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="bg-gray-800/50 border-t border-gray-700 p-6 flex items-center justify-end gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-2.5 rounded-lg text-sm font-bold text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={createTask}
                            disabled={saving}
                            className="px-6 py-2.5 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20 transition-all transform active:scale-95"
                        >
                            {saving ? 'Creating...' : 'Create Task'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskCreatePage;
